import { useEffect, useState } from 'react';
import { AlertTriangle, Banknote, CheckCircle2, CreditCard, Loader2, Star, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { formatUsd } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useGroupPay,
  useGroupPayQuote,
  useSetGroupDefaultMethod,
  type GroupPayQuote,
  type GroupPayResult,
} from '@/hooks/useGroupPay';

/**
 * Pay selected invoices across a chain's stores.
 *
 * The modal is deliberately three screens, not one: a QUOTE the payer reads
 * before committing (which stores, how much each, which card), the run, and a
 * RESULT that reports each store separately.
 *
 * ⚠ That last part is not defensive padding. The backend charges once per
 * store, so a run genuinely can half-succeed — showing a single "paid ✓" over
 * 26 charges where two declined would be a lie the payer only discovers on a
 * statement.
 */

export function ChainPayModal({
  invoiceIds,
  onClose,
}: {
  invoiceIds: string[];
  onClose: () => void;
}) {
  const quote = useGroupPayQuote();
  const pay = useGroupPay();
  const setDefault = useSetGroupDefaultMethod();
  const [pmId, setPmId] = useState<string>('');
  // Held locally rather than re-read from the quote: changing the main payer
  // mid-screen must not re-quote, which would re-read the mirror underneath
  // someone who is part-way through reading the numbers.
  const [defaultPmId, setDefaultPmId] = useState<string | null>(null);
  const [result, setResult] = useState<GroupPayResult | null>(null);

  const quoteMutate = quote.mutate;
  useEffect(() => {
    quoteMutate(invoiceIds, {
      onSuccess: (q) => {
        setDefaultPmId(q.default_pm_id);
        // The MAIN PAYER is what a run charges, so it is what the screen
        // opens on. A sole method is the same answer by another route.
        setPmId(q.default_pm_id ?? (q.methods.length === 1 ? q.methods[0]!.stripe_pm_id : ''));
      },
    });
    // Quote once per opening — re-quoting on every render would re-read the
    // mirror while the payer is reading the numbers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const q: GroupPayQuote | undefined = quote.data;
  const busy = pay.isPending;

  function run() {
    if (!pmId) return;
    pay.mutate({ invoiceIds, pmId }, { onSuccess: (r) => setResult(r) });
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-xl rounded-2xl bg-brix-surface shadow-card-deep overflow-hidden flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-brix-border bg-brix-surface-alt/60">
          <h2 className="text-lg font-bold text-brix-ink">
            {result ? 'Payment result' : 'Pay selected invoices'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-1.5 rounded-lg text-brix-muted hover:bg-brix-primary-tint hover:text-brix-ink transition-colors disabled:opacity-40"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {quote.isPending && (
            <p className="py-8 text-center text-sm text-brix-muted">
              <Loader2 size={16} className="animate-spin inline-block mr-2" />
              Adding it up…
            </p>
          )}

          {quote.error && <Problem>{quote.error.message}</Problem>}

          {result ? (
            <ResultView result={result} />
          ) : (
            q && (
              <>
                <div className="rounded-xl border border-brix-border bg-brix-surface-alt/40 px-4 py-3">
                  <p className="text-[11px] uppercase tracking-wide text-brix-muted font-semibold">
                    Total
                  </p>
                  <p className="font-display text-2xl font-bold text-brix-ink tabular-nums">
                    {formatUsd(q.total)}
                  </p>
                  <p className="text-sm text-brix-muted mt-0.5">
                    {q.invoice_count} invoice{q.invoice_count === 1 ? '' : 's'} across{' '}
                    {q.store_count} store{q.store_count === 1 ? '' : 's'}
                  </p>
                </div>

                <div className="rounded-xl border border-brix-border overflow-hidden">
                  <table className="w-full text-sm">
                    <tbody>
                      {q.stores.map((s) => (
                        <tr
                          key={s.customer_id}
                          className="border-b border-brix-border/70 last:border-b-0"
                        >
                          <td className="px-4 py-2.5">
                            <span className="font-medium text-brix-ink">{s.store_name}</span>
                            <span className="block text-[11px] text-brix-muted">
                              {s.invoice_count} invoice{s.invoice_count === 1 ? '' : 's'}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-semibold">
                            {formatUsd(s.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {q.skipped_in_flight.length > 0 && (
                  <Note tone="amber">
                    {q.skipped_in_flight.length} of the invoices you picked already have a
                    payment in flight — they are not included above and will not be charged
                    again.
                  </Note>
                )}

                {q.charge_note && <Note tone="slate">{q.charge_note}</Note>}

                {q.methods.length === 0 ? (
                  <Note tone="amber">
                    <span className="font-semibold">{q.pay_from.name}</span> has no saved
                    payment method yet. Add a card or bank under Settings → Payment methods
                    on this account, then come back.
                  </Note>
                ) : (
                  <div className="space-y-2">
                    <p className="text-[11px] uppercase tracking-wide text-brix-muted font-semibold">
                      Pay from {q.pay_from.name}
                    </p>
                    {q.methods.map((m) => {
                      const isMain = defaultPmId === m.stripe_pm_id;
                      return (
                        <div
                          key={m.stripe_pm_id}
                          className={cn(
                            'rounded-xl border px-4 py-3 transition-colors',
                            pmId === m.stripe_pm_id
                              ? 'border-brix-primary bg-brix-primary-tint/40'
                              : 'border-brix-border hover:bg-brix-primary-tint/20',
                          )}
                        >
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="radio"
                              name="chain-pay-method"
                              checked={pmId === m.stripe_pm_id}
                              onChange={() => setPmId(m.stripe_pm_id)}
                            />
                            {m.kind === 'ach' ? <Banknote size={16} /> : <CreditCard size={16} />}
                            <span className="text-sm text-brix-ink">
                              {m.brand ?? (m.kind === 'ach' ? 'Bank account' : 'Card')}
                              {m.last4 ? ` ···${m.last4}` : ''}
                            </span>
                            {isMain && (
                              <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-brix-primary-tint px-2 py-0.5 text-[11px] font-semibold text-brix-primary">
                                <Star size={11} /> Main payer
                              </span>
                            )}
                          </label>
                          {!isMain && (
                            <button
                              type="button"
                              onClick={() =>
                                setDefault.mutate(m.stripe_pm_id, {
                                  onSuccess: () => setDefaultPmId(m.stripe_pm_id),
                                })
                              }
                              disabled={busy || setDefault.isPending}
                              className="mt-1 ml-7 text-[12px] font-medium text-brix-primary hover:underline disabled:opacity-40"
                            >
                              Make this the main payer
                            </button>
                          )}
                        </div>
                      );
                    })}

                    {/* Which card gets charged when nobody picks is a real
                        setting, so the screen says whether it exists rather
                        than quietly charging whichever card sorted first. */}
                    <p className="text-[12px] text-brix-muted pt-0.5">
                      {defaultPmId ? (
                        <>
                          The main payer is what {q.pay_from.name} is charged on by default —
                          here and on its own invoices.
                        </>
                      ) : (
                        <>
                          No main payer set for {q.pay_from.name} yet. Pick one above and it
                          will be selected for you next time.
                        </>
                      )}
                    </p>

                    {setDefault.error && <Problem>{setDefault.error.message}</Problem>}
                  </div>
                )}

                {pay.error && <Problem>{pay.error.message}</Problem>}
              </>
            )
          )}
        </div>

        <div className="px-5 py-4 border-t border-brix-border flex justify-end gap-2">
          {result ? (
            <Button onClick={onClose}>Done</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button
                onClick={run}
                disabled={busy || !q?.payments_live || !pmId || (q?.total ?? 0) <= 0}
              >
                {busy ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Paying…
                  </>
                ) : (
                  <>Pay {formatUsd(q?.total ?? 0)}</>
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultView({ result }: { result: GroupPayResult }) {
  const trouble = result.stores.filter((s) => s.status === 'failed' || s.status === 'skipped');
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-brix-border bg-brix-surface-alt/40 px-4 py-3">
        <p className="font-display text-2xl font-bold text-brix-ink tabular-nums">
          {formatUsd(result.charged_total)}
        </p>
        <p className="text-sm text-brix-muted mt-0.5">
          {result.succeeded} paid
          {result.processing > 0 &&
            ` · ${result.processing} clearing (bank payments take a few days)`}
          {result.failed > 0 && ` · ${result.failed} didn't go through`}
        </p>
      </div>

      {trouble.length > 0 && (
        <Note tone="amber">
          These stores were <span className="font-semibold">not</span> paid — everything else
          above went through, so only these need another try:
          <ul className="mt-2 space-y-1">
            {trouble.map((s) => (
              <li key={s.customer_id}>
                <span className="font-medium">{s.store_name}</span> — {s.error ?? 'declined'}
              </li>
            ))}
          </ul>
        </Note>
      )}

      {result.remaining.length > 0 && (
        <Note tone="amber">
          {result.remaining.length} invoice{result.remaining.length === 1 ? '' : 's'} were not
          reached. Nothing was charged for them — select them again to finish.
        </Note>
      )}

      <div className="rounded-xl border border-brix-border overflow-hidden">
        <table className="w-full text-sm">
          <tbody>
            {result.stores.map((s) => (
              <tr key={s.customer_id} className="border-b border-brix-border/70 last:border-b-0">
                <td className="px-4 py-2.5">
                  <span className="font-medium text-brix-ink">{s.store_name}</span>
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">{formatUsd(s.amount)}</td>
                <td className="px-4 py-2.5 text-right">
                  {s.status === 'succeeded' ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-[13px] font-semibold">
                      <CheckCircle2 size={13} /> Paid
                    </span>
                  ) : s.status === 'processing' ? (
                    <span className="text-[13px] text-brix-muted">Clearing</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-brix-warning text-[13px] font-semibold">
                      <AlertTriangle size={13} /> Not paid
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-brix-muted">
        Each store is paid on its own account, so each one gets its own QuickBooks payment
        and its own receipt. Invoices flip to Paid once QuickBooks catches up.
      </p>
    </div>
  );
}

function Note({ tone, children }: { tone: 'amber' | 'slate'; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'rounded-xl border px-4 py-3 text-sm leading-relaxed',
        tone === 'amber'
          ? 'border-amber-200 bg-amber-50 text-amber-900'
          : 'border-brix-border bg-brix-surface-alt/40 text-brix-muted',
      )}
    >
      {children}
    </div>
  );
}

function Problem({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-brix-warning/30 bg-brix-warning/5 px-4 py-3 text-sm text-brix-warning">
      {children}
    </div>
  );
}
